import React from "react";
import { tokens, Combobox, Option } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { Customer } from "../../interfaces/Customer";
import { useEditorStyles } from "../Styles/Editor";
import { useRigths } from "../Tools/ObjectLoader";
import { IsNeomyticConsultant } from "../../interfaces/Consultant";

type CustomerDropdownProps = {
    setCustomer: (customer?: Customer) => void;
    currentCustomer?: Customer;
    customers: Customer[];
    disabled?: boolean;
    ForceLoggedInConsultant?: boolean;
};

export const CustomerDropdown: React.FC<CustomerDropdownProps> = ({ setCustomer, currentCustomer, customers, disabled, ForceLoggedInConsultant }) => {
    const { consultantid } = useRigths();
    const CustomerList = React.useMemo(() => {
        if (ForceLoggedInConsultant && consultantid) {
            return customers.filter(x => (x.consultantid === consultantid || x.sharedWithAllConsultants) && x.peppolIdentifier);
        }
        return customers.filter(x => (IsNeomyticConsultant(x.consultantid) || x.sharedWithAllConsultants));
    }, [customers, ForceLoggedInConsultant, consultantid]);
    // Filters
    const [Filter, setFilter] = React.useState<Customer[]>([]);
    const [Search, setSearch] = React.useState<string | undefined>();
    // Other
    const { t } = useTranslation();
    const styles = useEditorStyles();
    React.useEffect(() => {
        if (CustomerList !== undefined) { setFilter(CustomerList.filter(c => c.isActif)); }
    }, [CustomerList]);
    return <Combobox
        style={disabled ? {
            color: tokens.colorNeutralForeground1, // text color 
            WebkitTextFillColor: tokens.colorNeutralForeground1, // required for Chrome
        } : {}}
        className={styles.fluid}
        positioning="below"
        listbox={{
            className: styles.optionContainer,
        }}
        freeform
        placeholder={t('SelectCustomer')}
        onChange={(event) => {
            const value = event.target.value.trim();
            const matches = CustomerList.filter(
                (c) => c.isActif
                    && (
                        ((c.displayName !== null && c.displayName.toLowerCase().indexOf(value.toLowerCase()) !== -1)
                            || (c.name !== null && c.name.toLowerCase().indexOf(value.toLowerCase()) !== -1))
                    )
            );
            setFilter(matches);
            if (value.length && matches.length < 1) {
                setSearch(value);
                setCustomer(undefined);
            } else if (value.length && matches.length === 1) {
                setSearch(value);
                setCustomer(matches[0]);
            } else {
                setSearch(undefined);
                setCustomer(undefined);
            }
        }}
        onOptionSelect={(event, data) => {
            if (data.optionValue) {
                const matchingOption = CustomerList.find(c => c.isActif && c.customerid === data.optionValue);
                if (matchingOption) {
                    setSearch(undefined);
                    if (matchingOption.customerid !== currentCustomer?.customerid) {
                        setCustomer(matchingOption);
                    }
                    setFilter(CustomerList.filter(c => c.isActif));
                } else {
                    setSearch(data.optionText);
                }
            }
        }}
        defaultValue={currentCustomer && currentCustomer.displayName ? currentCustomer.displayName : undefined}
        defaultSelectedOptions={currentCustomer && currentCustomer.customerid ? [currentCustomer.customerid] : undefined}
        disabled={disabled}
    >
        {Search ? (
            <Option key="freeform" text={Search}>
                Search for "{Search}"
            </Option>
        ) : null}
        {Filter.map((p) => (
            <Option key={p.customerid} value={p.customerid !== null ? p.customerid : ""}>{p.displayName !== null ? p.displayName : "-- ? --"}</Option>
        ))}
    </Combobox>;
};